import React, { useEffect, useState } from 'react';
import { auth } from '../../firebaseConfig';
import { PiMagnifyingGlass, PiSpinner, PiFileText } from 'react-icons/pi';

const FUNCTIONS_BASE = 'https://us-central1-up-convert.cloudfunctions.net';

export function Summaries({ currentUser }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSearch = async (e) => {
        if (e) e.preventDefault();

        setError(null);
        setLoading(true);

        try {
            if (!auth.currentUser) return;
            const idToken = await auth.currentUser.getIdToken();

            const res = await fetch(`${FUNCTIONS_BASE}/searchOrgMeetings?q=${encodeURIComponent(query)}`, {
                headers: { Authorization: `Bearer ${idToken}` }
            });

            if (!res.ok) {
                throw new Error('An error occurred.');
            }

            const data = await res.json();
            setResults(data.results || []);
        } catch (err) {
            setError('An error occurred.');
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    // Run empty search on mount to load initial latest summaries
    useEffect(() => {
        handleSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatDate = (ms) => {
        if (!ms) return 'Unknown Date';
        return new Date(ms).toLocaleDateString(undefined, {
            month: 'short', day: 'numeric', year: 'numeric'
        });
    };

    return (
        <section className="dash-stack">


            <article className="dash-card">
                <form onSubmit={handleSearch} style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                    <div style={{ flex: 1, position: 'relative' }}>
                        <PiMagnifyingGlass style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input
                            type="text"
                            placeholder="Search by lead domain (upconvert.co) or user email..."
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 10px 10px 36px',
                                borderRadius: '6px',
                                border: '1px solid var(--border-color)',
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-color)',
                                fontSize: '14px'
                            }}
                        />
                    </div>
                    <button type="submit" className="primary-button" disabled={loading} style={{ padding: '0 24px' }}>
                        {loading ? <PiSpinner className="spin" /> : 'Search'}
                    </button>
                </form>

                {error && <div className="error-text" style={{ marginBottom: '16px' }}>{error}</div>}

                <div className="dash-grid" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
                    {results.map((meeting) => (
                        <div key={meeting.id} style={{
                            padding: '16px',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            backgroundColor: 'var(--bg-secondary)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div className="text-muted" style={{ fontSize: '14px', display: 'flex', gap: '8px', alignItems: 'center', fontWeight: 500, color: 'var(--text-color)' }}>
                                    <span>{(meeting.participants || []).join(', ') || 'Unknown Participants'}</span>
                                    <span style={{ color: 'var(--text-muted)' }}>• {formatDate(meeting.createdAtMs)}</span>
                                </div>
                            </div>

                            <div style={{ padding: '12px', backgroundColor: 'var(--bg-primary)', borderRadius: '6px', fontSize: '14px', lineHeight: 1.5 }}>
                                {meeting.summary ? (
                                    <div style={{ whiteSpace: 'pre-wrap' }}>
                                        {typeof meeting.summary === 'object' ? (
                                            <>
                                                <div>{meeting.summary.summary}</div>
                                                {meeting.summary.nextSteps && (
                                                    <div style={{ marginTop: '8px' }}>
                                                        <strong>Next Steps:</strong>
                                                        <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                                                            {meeting.summary.nextSteps.rep && meeting.summary.nextSteps.rep.length > 0 && (
                                                                <li><strong>Rep:</strong> {meeting.summary.nextSteps.rep.join(', ')}</li>
                                                            )}
                                                            {meeting.summary.nextSteps.client && meeting.summary.nextSteps.client.length > 0 && (
                                                                <li><strong>Client:</strong> {meeting.summary.nextSteps.client.join(', ')}</li>
                                                            )}
                                                        </ul>
                                                    </div>
                                                )}
                                                {meeting.summary.objections && meeting.summary.objections.length > 0 && (
                                                    <div style={{ marginTop: '8px' }}>
                                                        <strong>Objections:</strong>
                                                        <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px' }}>
                                                            {meeting.summary.objections.map((obj, i) => (
                                                                <li key={i}><strong>{obj.point}:</strong> {obj.context}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            meeting.summary
                                        )}
                                    </div>
                                ) : (
                                    <span className="text-muted" style={{ fontStyle: 'italic' }}>No summary generated for this meeting.</span>
                                )}
                            </div>
                        </div>
                    ))}

                    {!loading && results.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                            <PiFileText size={48} style={{ opacity: 0.5, marginBottom: '12px' }} />
                            <p>No call summaries found.</p>
                        </div>
                    )}
                </div>
            </article>
        </section>
    );
}
